import { useRef, useState } from 'react';
import { Button, IconButton, Select, TextArea, TextField, useToast, useT } from '@dugzi/ui';
import { FileUp, Plus, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { MONTHS, formOf, useData, writableBooks } from '../lib/data';
import type { Labelled, Person, PersonForm } from '../lib/types';
import { readImportFile } from '../lib/import';
import { AddressFields } from './AddressFields';

const EMPTY: PersonForm = { given: '', family: '', org: null, emails: [], phones: [], addresses: [], birthday: null, howWeMet: null, notes: null, labels: [] };

/** Add or edit someone. Only a name is needed; everything else is optional. A .vcf card someone sent
 *  can fill the form (shown for review; nothing is saved until Save). */
export function PersonEditor({ person, defaultBook, presetLabel, onDone, onCancel }: { person: Person | null; defaultBook: string; presetLabel?: string; onDone(id: string): void; onCancel(): void }) {
  const { books } = useData();
  const toast = useToast();
  const t = useT();
  const [revision, setRevision] = useState(person?.version);
  const [conflict, setConflict] = useState(false);
  const [f, setF] = useState<PersonForm>(() => (person ? formOf(person) : { ...EMPTY, labels: presetLabel ? [presetLabel] : [] }));
  const [book, setBook] = useState(defaultBook);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const targets = writableBooks(books);
  const set = <K extends keyof PersonForm>(k: K, v: PersonForm[K]) => setF((x) => ({ ...x, [k]: v }));

  // Day, month and an optional year, kept apart while someone fills them in.
  const [bday, setBdayParts] = useState(() => parseBirthday(f.birthday));
  const setBday = (part: 'd' | 'm' | 'y', v: string) => {
    const n = { ...bday, [part]: v };
    setBdayParts(n);
    set('birthday', n.d && n.m ? `${n.y && /^\d{4}$/.test(n.y) ? n.y : '-'}-${n.m.padStart(2, '0')}-${n.d.padStart(2, '0')}` : null);
  };

  const save = async () => {
    if (busy || conflict) return;
    setBusy(true); setError(null);
    const body = { ...f, emails: f.emails.filter((e) => e.value.trim()), phones: f.phones.filter((e) => e.value.trim()), addresses: f.addresses.filter((e) => e.value.trim() || Object.entries(e.parts ?? {}).some(([k, v]) => k !== 'country' && v?.trim())) };
    try {
      if (person) { await api(`/api/people/${person.id}`, { method: 'PATCH', body: { ...body, version: revision } }); toast.show('Saved.'); onDone(person.id); }
      else { const r = await api<{ id: string }>('/api/people', { body: { ...body, book } }); toast.show(`${f.given || f.family || 'They'} saved.`); onDone(r.id); }
    } catch (e) {
      setError((e as Error).message);
      if (e instanceof ApiError && e.status === 412) setConflict(true);
    }
    setBusy(false);
  };
  const reload = async () => {
    if (!person || busy) return;
    setBusy(true);
    try {
      const latest = await api<Person>(`/api/people/${person.id}`);
      setF(formOf(latest));
      setBdayParts(parseBirthday(latest.birthday));
      setRevision(latest.version);
      setConflict(false); setError(null);
    } catch (e) { setError((e as Error).message); }
    setBusy(false);
  };
  const openCard = async (fl: File) => {
    try {
      const limits = await api<{ importBytes: number }>('/api/import/csv');
      const vcf = await readImportFile(fl, limits.importBytes, t);
      const r = await api<PersonForm>('/api/vcard/parse', { body: { vcf } });
      setF((x) => ({ ...x, ...r, emails: r.emails ?? [], phones: r.phones ?? [], addresses: r.addresses ?? [], labels: x.labels }));
      setBdayParts(parseBirthday(r.birthday ?? null));
      toast.show('Card opened. Check it, then Save.');
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <form className="editor" onSubmit={(e) => { e.preventDefault(); save(); }} aria-label={person ? `Edit ${person.display}` : 'New contact'} data-testid="person-editor">
      <h2>{person ? `Edit ${person.display}` : 'New contact'}</h2>
      {!person && (
        <div className="editor__row">
          {targets.length > 1 && <Select label="Address book" value={book} onValueChange={(v) => setBook(String(v))} options={targets.map((b) => ({ value: b.id, label: b.name, hint: b.shared ? 'Shared' : 'Only you' }))} testId="editor-book" />}
          <Button size="sm" variant="ghost" icon={<FileUp aria-hidden />} onClick={() => file.current?.click()}>Open a .vcf card</Button>
          <input ref={file} type="file" accept=".vcf,text/vcard" hidden onChange={(e) => { const fl = e.target.files?.[0]; if (fl) openCard(fl); e.target.value = ''; }} aria-label="Open a .vcf card" />
        </div>
      )}
      <div className="editor__grid">
        <TextField label="First name" value={f.given} onChange={(e) => set('given', e.target.value)} autoFocus={!person} autoComplete="off" data-testid="given" />
        <TextField label="Last name" value={f.family} onChange={(e) => set('family', e.target.value)} autoComplete="off" data-testid="family" />
      </div>
      <TextField label="How we met" hint="A few words for later: “Mia’s swimming club”." value={f.howWeMet ?? ''} onChange={(e) => set('howWeMet', e.target.value || null)} data-testid="how-we-met" />
      <Multi label="Email" kind="email" items={f.emails} onChange={(v) => set('emails', v)} testId="email" />
      <Multi label="Phone" kind="tel" items={f.phones} onChange={(v) => set('phones', v)} testId="phone" />
      <AddressFields items={f.addresses} onChange={(v) => set('addresses', v)} />
      <fieldset className="editor__bday">
        <legend>Birthday</legend>
        <Select label="Day" value={bday.d} onValueChange={(v) => setBday('d', String(v))} placeholder="Day" options={[{ value: '', label: '—' }, ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))]} testId="bday-day" />
        <Select label="Month" value={bday.m} onValueChange={(v) => setBday('m', String(v))} placeholder="Month" options={[{ value: '', label: '—' }, ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))]} testId="bday-month" />
        <TextField label="Year (optional)" inputMode="numeric" maxLength={4} value={bday.y} onChange={(e) => setBday('y', e.target.value.replace(/\D/g, ''))} data-testid="bday-year" />
      </fieldset>
      <TextField label="Organisation" value={f.org ?? ''} onChange={(e) => set('org', e.target.value || null)} />
      <TextField label="Labels" hint="Separate with commas: Football parents, Neighbours." value={f.labels.join(', ')} onChange={(e) => set('labels', e.target.value.split(',').map((x) => x.trimStart()))}
        onBlur={() => set('labels', f.labels.map((x) => x.trim()).filter(Boolean))} data-testid="labels" />
      <TextArea label="Notes" value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value || null)} rows={3} />
      {error && <p role="alert" className="editor__error" data-testid="editor-error">{error}</p>}
      {conflict && <Button variant="ghost" disabled={busy} onClick={reload}>Discard my edits and load latest</Button>}
      <div className="editor__actions">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" loading={busy} disabled={conflict} data-testid="save-person">Save</Button>
      </div>
    </form>
  );
}

function parseBirthday(b: string | null): { d: string; m: string; y: string } {
  const m = b ? /^(\d{4}|-)-(\d{2})-(\d{2})$/.exec(b) : null;
  return m ? { y: m[1] === '-' ? '' : m[1], m: String(Number(m[2])), d: String(Number(m[3])) } : { d: '', m: '', y: '' };
}

/** A list of values with an optional label each (emails, phones, addresses). */
function Multi({ label, kind, items, onChange, testId }: { label: string; kind: 'email' | 'tel' | 'text'; items: Labelled[]; onChange(v: Labelled[]): void; testId: string }) {
  const rows = items.length ? items : [{ value: '', label: null }];
  const put = (i: number, v: Labelled) => onChange(rows.map((x, j) => (j === i ? v : x)));
  return (
    <div className="multi">
      {rows.map((it, i) => (
        <div key={i} className="multi__row">
          <TextField label={i === 0 ? label : `${label} ${i + 1}`} type={kind} value={it.value} onChange={(e) => put(i, { ...it, value: e.target.value })} autoComplete="off" data-testid={`${testId}-${i}`} />
          <TextField label="Kind" placeholder="home, work…" value={it.label ?? ''} onChange={(e) => put(i, { ...it, label: e.target.value || null })} />
          {rows.length > 1 && <IconButton label={`Remove ${label.toLowerCase()} ${i + 1}`} icon={<X aria-hidden size={16} />} onClick={() => onChange(rows.filter((_, j) => j !== i))} />}
        </div>
      ))}
      <Button size="sm" variant="ghost" icon={<Plus aria-hidden />} onClick={() => onChange([...rows, { value: '', label: null }])}>Add {label.toLowerCase()}</Button>
    </div>
  );
}
